import CustomDateTimePicker from 'Components/CustomDateTimePicker'
import CustomLoadingButton from 'Components/CustomLoadingButton'
import InfoTooltip from 'Components/InfoTooltip'
import PTAFormField from 'Components/PTAFormField'
import StyledForm from 'Components/StyledForm/StyledForm'
import StyledLabel from 'Components/StyledLabel'
import { WithLoader } from 'Components/WithLoader'
import SPMAllowedApiClients from 'Services/ServiceProviderManagement/Features/AllowedClient/Components/SPMAllowedApiClients'
import { SELECTED_SALES_POINT_PARAMETER_KEY } from 'Services/ServiceProviderManagement/Features/SalesPoint/Hooks/useSalesPointsWithParams'
import {
  useCreateSingleSalesPointMutation,
  useUpdateSingleSalesPointMutation
} from 'Services/ServiceProviderManagement/Features/SalesPoint/salesPointApi'
import {
  type SalesPoint,
  type SalesPointAddress,
  SalesPointUsage
} from 'Services/ServiceProviderManagement/Features/SalesPoint/types'
import { SELECTED_SERVICE_PROVIDER_PARAMETER_KEY } from 'Services/ServiceProviderManagement/Features/ServiceProvider/Hooks/useServiceProvidersWithParams'
import { SERVICE_PROVIDER_DETAILS_DRAWER_TAB_PARAM_KEY } from 'Services/ServiceProviderManagement/Features/ServiceProvider/Pages/ServiceProviderListPage'
import { useGetAllServiceProvidersQuery } from 'Services/ServiceProviderManagement/Features/ServiceProvider/serviceProviderApi'
import { ServiceProviderTabType } from 'Services/ServiceProviderManagement/Features/ServiceProvider/types'
import type { FieldValidationErrors } from 'Services/ServiceProviderManagement/types'
import { useTypedSelector } from 'Store'
import type { ServiceProviderManagementError } from 'Util/ApiClient'
import { CopyObjectKeys, useCopyObjectContext } from 'Util/Context/copyObjectContext'
import {
  IS_HSL_SERVICE_ENVIRONMENT,
  IS_WALTTI_SERVICE_ENVIRONMENT,
  PTA_PARAM_KEY,
  SELECTED_PTA_STORAGE_KEY,
  SERVICE_PROVIDER_MANAGEMENT_ROOT_URL,
  SORT_ORDER_ASC
} from 'Util/constants'
import { formatToUTCISO } from 'Util/formatters'
import { useValidationErrors } from 'Util/hooks/useValidationErrors'
import SnackbarUtils from 'Util/Snackbar'
import { appendQueryParamsToUrl } from 'Util/url'
import {
  Autocomplete,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { t } from 'ttag'

const fieldErrorsDefaultState: FieldValidationErrors = {
  service_provider_id: [],
  sales_point_id: [],
  usage: [],
  start_date: [],
  external_identifier: [],
  name: [],
  customer_reference_name: [],
  s365t_account_id: [],
  business_id: [],
  additional_info: [],
  sales_allowed: [],
  hsl_sales_point: [],
  billed_at_sales_point_level: [],
  transfer_sales_to_financial_system: [],
  phone: [],
  street_address: [],
  postal_code: [],
  city: [],
  source: []
}

interface SalesPointFormProps {
  salesPoint?: SalesPoint
  successBaseUrl: string
}

function SalesPointForm({ salesPoint, successBaseUrl }: Readonly<SalesPointFormProps>) {
  const navigate = useNavigate()
  const isEdit = !!salesPoint
  const { user } = useTypedSelector(state => state.user)

  const [searchParams] = useSearchParams()
  const selectedServiceProviderId = searchParams.get(SELECTED_SERVICE_PROVIDER_PARAMETER_KEY)

  const { fieldErrors, setValidationErrors, getFieldErrorHelperText } =
    useValidationErrors(fieldErrorsDefaultState)

  const { getCopiedObject, clearCopiedObject } = useCopyObjectContext()
  const copiedSalesPointData = isEdit
    ? null
    : getCopiedObject<SalesPoint>(CopyObjectKeys.SALES_POINT)

  const salesPointDefaultValues: Partial<SalesPoint> = {
    pta: IS_WALTTI_SERVICE_ENVIRONMENT
      ? (localStorage.getItem(SELECTED_PTA_STORAGE_KEY) ?? undefined)
      : undefined,
    service_provider_id: selectedServiceProviderId ?? undefined,
    sales_allowed: true,
    active: true,
    ...(IS_HSL_SERVICE_ENVIRONMENT
      ? {
          hsl_sales_point: false,
          billed_at_sales_point_level: false,
          transfer_sales_to_financial_system: false
        }
      : {})
  }

  const [salesPointDetails, setSalesPointDetails] = useState<Partial<SalesPoint>>(
    (isEdit && salesPoint) || copiedSalesPointData || { ...salesPointDefaultValues }
  )

  // biome-ignore lint/correctness/useExhaustiveDependencies: suppress clearCopiedObject, suppress copiedSalesPointData
  useEffect(() => {
    if (copiedSalesPointData) {
      clearCopiedObject(CopyObjectKeys.SALES_POINT)
    }
  }, [])

  const [createSingleSalesPoint, createStatus] = useCreateSingleSalesPointMutation()
  const [updateSingleSalesPoint, updateStatus] = useUpdateSingleSalesPointMutation()

  const mutationStatus = isEdit ? updateStatus : createStatus
  const { isLoading: isSubmitLoading } = mutationStatus

  const { data: allServiceProviders, isLoading: isAllServiceProvidersLoading } =
    useGetAllServiceProvidersQuery({
      sort_by: 'serviceProviderId',
      sort_order: SORT_ORDER_ASC,
      pta: salesPointDetails.pta ?? undefined
    })
  const serviceProviderOptions = (allServiceProviders?.resources ?? []).map(serviceProvider => {
    return {
      value: serviceProvider.service_provider_id,
      label: serviceProvider.name
    }
  })

  const [selectedReadClients, setSelectedReadClients] = useState<string[]>(
    salesPointDetails?.client_read ?? []
  )
  const [selectedWriteClients, setSelectedWriteClients] = useState<string[]>(
    salesPointDetails?.client_write ?? []
  )

  const handleChange = (
    value: string | string[] | number | boolean | undefined,
    field: keyof SalesPoint
  ) => {
    if (value !== undefined) {
      setSalesPointDetails(prevState => ({ ...prevState, [field]: value }) as SalesPoint)
    }
  }

  const handleDateTimePickerChange = (value: Date | null, name: string) => {
    let formattedValue = ''
    if (value instanceof Date) {
      formattedValue = formatToUTCISO(value)
    }

    setSalesPointDetails(prevState => ({
      ...prevState,
      [name]: formattedValue
    }))
  }

  const handleAddressChange = (value: string, field: keyof SalesPointAddress) => {
    setSalesPointDetails(prevState => ({
      ...prevState,
      address: { ...prevState.address, [field]: value } as SalesPointAddress
    }))
  }

  const usageOptions = [
    {
      label: t`Production`,
      value: SalesPointUsage.PROD
    },
    {
      label: t`Test`,
      value: SalesPointUsage.TEST
    }
  ]

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const salesPointData = {
      ...salesPointDetails,
      client_read: selectedReadClients,
      client_write: selectedWriteClients
    }

    const mutation = isEdit
      ? updateSingleSalesPoint({
          ...salesPointData,
          sales_point_id: salesPoint.sales_point_id
        })
      : createSingleSalesPoint({
          ...salesPointData,
          pta: IS_HSL_SERVICE_ENVIRONMENT ? user?.pta : salesPointData.pta
        })

    mutation
      .unwrap()
      .then((salesPointResponse: SalesPoint) => {
        SnackbarUtils.success(t`Sales point successfully saved.`)
        if (selectedServiceProviderId) {
          const urlParams = `${SELECTED_SERVICE_PROVIDER_PARAMETER_KEY}=${selectedServiceProviderId}&${SERVICE_PROVIDER_DETAILS_DRAWER_TAB_PARAM_KEY}=${ServiceProviderTabType.SALES_POINTS}`
          navigate(`${SERVICE_PROVIDER_MANAGEMENT_ROOT_URL}/service-providers/?${urlParams}`)
        } else {
          navigate(
            appendQueryParamsToUrl(successBaseUrl, {
              [SELECTED_SALES_POINT_PARAMETER_KEY]: salesPointResponse.sales_point_id,
              [PTA_PARAM_KEY]: salesPointResponse.pta
            })
          )
        }
      })
      .catch(({ data: errorResponse }: { data: ServiceProviderManagementError }) => {
        const { violations } = errorResponse
        setValidationErrors(violations)
        SnackbarUtils.error(t`There was an error saving the sales point.`)
      })
  }

  const isBilledAtSalesPointLevelDisabled = !isEdit || !salesPointDetails.s365t_account_id

  return (
    <WithLoader loading={isAllServiceProvidersLoading}>
      <Grid container spacing={2} mb={4}>
        <Grid size={12}>
          <StyledForm onSubmit={handleSubmit} isDisabled={isSubmitLoading}>
            {!isEdit && IS_WALTTI_SERVICE_ENVIRONMENT && (
              <Box>
                <PTAFormField
                  value={salesPointDetails.pta}
                  onChange={value => handleChange(value, 'pta')}
                />
              </Box>
            )}
            <Box sx={{ display: 'flex' }}>
              <TextField
                sx={{ minWidth: 240, mr: 3 }}
                name="name"
                required
                label={t`Name`}
                size="small"
                value={salesPointDetails?.name ?? ''}
                slotProps={{ htmlInput: { maxLength: 40 } }}
                error={fieldErrors.name.length > 0}
                helperText={getFieldErrorHelperText('name')}
                onChange={e => handleChange(e.target.value, 'name')}
              />
              <TextField
                sx={{ minWidth: 240, mr: 3 }}
                name="sales_point_id"
                required
                label={t`Sales point id`}
                size="small"
                value={salesPointDetails?.sales_point_id ?? ''}
                slotProps={{ htmlInput: { maxLength: 9 } }}
                error={fieldErrors.sales_point_id.length > 0}
                helperText={getFieldErrorHelperText('sales_point_id')}
                onChange={e => handleChange(e.target.value, 'sales_point_id')}
              />
              <TextField
                sx={{ minWidth: 240, mr: 3 }}
                name="external_identifier"
                label={t`External identifier`}
                size="small"
                value={salesPointDetails?.external_identifier ?? ''}
                slotProps={{ htmlInput: { maxLength: 50 } }}
                error={fieldErrors.external_identifier.length > 0}
                helperText={getFieldErrorHelperText('external_identifier')}
                onChange={e => handleChange(e.target.value, 'external_identifier')}
              />
            </Box>
            <Box>
              <FormControl
                sx={{
                  minWidth: 280
                }}
              >
                <Autocomplete
                  disablePortal
                  size="small"
                  options={serviceProviderOptions}
                  renderOption={(props, option) => (
                    <MenuItem {...props} key={option.value}>
                      {option.label}
                    </MenuItem>
                  )}
                  renderInput={params => (
                    <TextField required {...params} label={t`Service provider`} />
                  )}
                  onChange={(_, newValue) =>
                    handleChange(newValue?.value as string, 'service_provider_id')
                  }
                  value={
                    serviceProviderOptions.find(
                      option => option.value === salesPointDetails.service_provider_id
                    ) || null
                  }
                />
                <FormHelperText error={fieldErrors.service_provider_id.length > 0}>
                  {getFieldErrorHelperText('service_provider_id')}
                </FormHelperText>
              </FormControl>
            </Box>
            <Box>
              <StyledLabel>{t`Start date`}</StyledLabel>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CustomDateTimePicker
                onChange={handleDateTimePickerChange}
                name="start_date"
                readOnly={isEdit}
                value={
                  salesPointDetails?.start_date ? new Date(salesPointDetails?.start_date) : null
                }
                label={t`Start date`}
                isRequired={true}
                isError={fieldErrors.start_date?.length > 0}
                helperText={getFieldErrorHelperText('start_date')}
              />
            </Box>
            <Box>
              <StyledLabel>{t`Business information`}</StyledLabel>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <TextField
                sx={{ minWidth: 240, mr: 3 }}
                name="business_id"
                label={t`Business id`}
                size="small"
                value={salesPointDetails?.business_id}
                slotProps={{ htmlInput: { maxLength: 9 } }}
                error={fieldErrors.business_id.length > 0}
                helperText={getFieldErrorHelperText('business_id')}
                onChange={e => handleChange(e.target.value, 'business_id')}
              />
              {IS_HSL_SERVICE_ENVIRONMENT && (
                <>
                  <TextField
                    sx={{ minWidth: 240, mr: 3 }}
                    name="s365t_account_id"
                    label={t`S365 account id`}
                    size="small"
                    value={salesPointDetails?.s365t_account_id}
                    error={fieldErrors.s365t_account_id.length > 0}
                    onChange={e =>
                      handleChange(e.target.value.replaceAll(/\D/g, ''), 's365t_account_id')
                    }
                    slotProps={{ htmlInput: { inputMode: 'numeric', maxLength: 30 } }}
                    helperText={getFieldErrorHelperText('s365t_account_id')}
                  />
                  <TextField
                    sx={{ minWidth: 240, mr: 3 }}
                    name="customer_reference_name"
                    label={t`Customer reference name`}
                    required
                    size="small"
                    value={salesPointDetails?.customer_reference_name}
                    error={fieldErrors.customer_reference_name.length > 0}
                    helperText={getFieldErrorHelperText('customer_reference_name')}
                    onChange={e => handleChange(e.target.value, 'customer_reference_name')}
                  />
                </>
              )}
            </Box>
            <Box>
              <StyledLabel>{t`Address`}</StyledLabel>
            </Box>
            <Box sx={{ display: 'flex', mb: 2 }}>
              <TextField
                sx={{ minWidth: 240, mr: 3 }}
                name="street_address"
                label={t`Street address`}
                size="small"
                value={salesPointDetails?.address?.street_address ?? ''}
                error={fieldErrors.street_address.length > 0}
                helperText={getFieldErrorHelperText('street_address')}
                onChange={e => handleAddressChange(e.target.value, 'street_address')}
              />
              <TextField
                sx={{ minWidth: 240, mr: 3 }}
                name="postal_code"
                label={t`Postal code`}
                size="small"
                value={salesPointDetails?.address?.postal_code ?? ''}
                error={fieldErrors.postal_code.length > 0}
                helperText={getFieldErrorHelperText('postal_code')}
                onChange={e => handleAddressChange(e.target.value, 'postal_code')}
              />
              <TextField
                sx={{ minWidth: 240, mr: 3 }}
                name="city"
                label={t`City`}
                size="small"
                value={salesPointDetails?.address?.city ?? ''}
                error={fieldErrors.city.length > 0}
                helperText={getFieldErrorHelperText('city')}
                onChange={e => handleAddressChange(e.target.value, 'city')}
              />
            </Box>
            <Box>
              <TextField
                sx={{ minWidth: 240 }}
                name="phone"
                label={t`Phone`}
                size="small"
                value={salesPointDetails?.phone}
                error={fieldErrors.phone.length > 0}
                helperText={getFieldErrorHelperText('phone')}
                onChange={e => handleChange(e.target.value, 'phone')}
              />
            </Box>
            <Box>
              <StyledLabel>{t`Usage`}</StyledLabel>
            </Box>
            <Box>
              <FormControl sx={{ minWidth: 240, mr: 3 }} required>
                <InputLabel size="small">{t`Usage`}</InputLabel>
                <Select
                  name="usage"
                  required
                  size="small"
                  label={t`Usage`}
                  value={salesPointDetails.usage ?? ''}
                  error={fieldErrors.usage.length > 0}
                  onChange={e => handleChange(e.target.value, 'usage')}
                >
                  {usageOptions.map(({ label, value }) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText error={fieldErrors.usage.length > 0}>
                  {getFieldErrorHelperText('usage')}
                </FormHelperText>
              </FormControl>
            </Box>
            <Box>
              <StyledLabel>{t`Sales`}</StyledLabel>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={salesPointDetails.sales_allowed}
                    onChange={e => handleChange(e.target.checked, 'sales_allowed')}
                    name="sales_allowed"
                    color="primary"
                  />
                }
                label={t`Sales allowed`}
              />
              <FormHelperText error={fieldErrors.sales_allowed.length > 0}>
                {getFieldErrorHelperText('sales_allowed')}
              </FormHelperText>
            </Box>
            {IS_HSL_SERVICE_ENVIRONMENT && (
              <>
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={salesPointDetails.hsl_sales_point}
                        onChange={e => handleChange(e.target.checked, 'hsl_sales_point')}
                        name="hsl_sales_point"
                        color="primary"
                      />
                    }
                    label={t`HSL sales point`}
                  />
                  <FormHelperText error={fieldErrors.hsl_sales_point.length > 0}>
                    {getFieldErrorHelperText('hsl_sales_point')}
                  </FormHelperText>
                </Box>
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={salesPointDetails.billed_at_sales_point_level}
                        onChange={e =>
                          handleChange(e.target.checked, 'billed_at_sales_point_level')
                        }
                        name="billed_at_sales_point_level"
                        color="primary"
                        disabled={!salesPointDetails.s365t_account_id}
                      />
                    }
                    label={t`Billed at sales point level`}
                  />
                  {!isEdit && (
                    <InfoTooltip
                      title={
                        <Typography variant="body2">
                          {t`Copied from the service provider to the new sales point.`}
                        </Typography>
                      }
                    />
                  )}

                  <FormHelperText error={fieldErrors.billed_at_sales_point_level.length > 0}>
                    {getFieldErrorHelperText('billed_at_sales_point_level')}
                  </FormHelperText>
                </Box>
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={salesPointDetails.transfer_sales_to_financial_system}
                        onChange={e =>
                          handleChange(e.target.checked, 'transfer_sales_to_financial_system')
                        }
                        name="transfer_sales_to_financial_system"
                        color="primary"
                        disabled={!salesPointDetails.s365t_account_id}
                      />
                    }
                    label={t`Transfer sales to financial system`}
                  />
                  <FormHelperText error={fieldErrors.transfer_sales_to_financial_system.length > 0}>
                    {getFieldErrorHelperText('transfer_sales_to_financial_system')}
                  </FormHelperText>
                </Box>
              </>
            )}
            <Box>
              <StyledLabel>{t`Source`}</StyledLabel>
              <TextField
                sx={{ minWidth: 240 }}
                name="source"
                label={t`Source`}
                size="small"
                value={salesPointDetails?.source}
                error={fieldErrors.source.length > 0}
                helperText={getFieldErrorHelperText('source')}
                onChange={e => handleChange(e.target.value, 'source')}
                slotProps={{ htmlInput: { maxLength: 256 } }}
              />
            </Box>
            <Box>
              <StyledLabel>{t`Additional information`}</StyledLabel>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <TextField
                sx={{ minWidth: 600 }}
                name="additional_info"
                label={t`Additional information`}
                size="small"
                value={salesPointDetails?.additional_info}
                error={fieldErrors.additional_info.length > 0}
                helperText={getFieldErrorHelperText('additional_info')}
                onChange={e => handleChange(e.target.value, 'additional_info')}
                multiline={true}
                rows={6}
              />
            </Box>
            <Box sx={{ maxWidth: '750px', py: 2 }}>
              <SPMAllowedApiClients
                selectedReadClients={selectedReadClients}
                selectedWriteClients={selectedWriteClients}
                setSelectedReadClients={setSelectedReadClients}
                setSelectedWriteClients={setSelectedWriteClients}
              />
            </Box>
            <CustomLoadingButton
              isLoading={isSubmitLoading}
              type="submit"
              size="large"
              iconPosition="end"
            >
              {t`Save sales point`}
            </CustomLoadingButton>
          </StyledForm>
        </Grid>
      </Grid>
    </WithLoader>
  )
}

export default SalesPointForm
